import type { SlotTag } from '../data/exercise-types';
import type { HealthDay } from '../lib/health';

export type Goal = 'fat_loss' | 'recomp' | 'muscle' | 'strength';

export const GOAL_LABEL: Record<Goal, string> = {
  fat_loss: 'Lose fat, keep muscle',
  recomp: 'Recomposition',
  muscle: 'Build muscle',
  strength: 'Get stronger',
};

export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active';

export interface SetLog {
  weightKg: number;
  reps: number;
  /** Reps in reserve, 6–10. Left blank is fine. */
  rpe?: number;
  done: boolean;
}

export interface LoggedExercise {
  exerciseId: string;
  slot: SlotTag;
  targetSets: number;
  targetReps: [number, number];
  /** Suggested working weight, from your own history. */
  suggestionKg?: number;
  suggestionReason?: string;
  sets: SetLog[];
  note?: string;
  /** Set when you swapped this out for something else mid-session. */
  swappedFrom?: string;
  skipped?: boolean;
  /**
   * Which warm-up steps you've ticked off. The ramp itself is recomputed from
   * your current working weight rather than stored, so editing the top set
   * updates the warm-ups too. Warm-ups deliberately live outside `sets` so they
   * never reach your volume totals or your progression history.
   */
  warmupsDone?: boolean[];
  /**
   * Exercises sharing a group id are run back-to-back with one rest after the
   * pair. Set by the generator; adjacent in the list by construction.
   */
  supersetGroup?: string;
}

export interface WorkoutSession {
  id: string;
  /** Local calendar date, YYYY-MM-DD. */
  date: string;
  startedAt: string;
  finishedAt?: string;
  splitId: string;
  templateId: string;
  templateName: string;
  blockNumber: number;
  weekInBlock: number;
  isDeload: boolean;
  exercises: LoggedExercise[];
  notes?: string;
  bodyweightKg?: number;
}

export interface CardioSession {
  id: string;
  date: string;
  typeId: string;
  minutes: number;
  distanceKm?: number;
  avgHr?: number;
  calories: number;
  /** False when you typed the number off the machine or your watch. */
  caloriesEstimated: boolean;
  rpe?: number;
  notes?: string;
}

export interface BodyMetric {
  id: string;
  date: string;
  weightKg?: number;
  waistCm?: number;
  chestCm?: number;
  armCm?: number;
  thighCm?: number;
  hipCm?: number;
  notes?: string;
}

export interface Profile {
  name: string;
  age: number;
  heightCm: number;
  bodyweightKg: number;
  sex: 'male' | 'female';
  goal: Goal;
  activityLevel: ActivityLevel;
  /** JS day numbers, 0 = Sunday. */
  liftDays: number[];
  cardioDays: number[];
  /** 24h local time, "18:00". */
  gymTime: string;
  remindersEnabled: boolean;
  reminderLeadMinutes: number;
}

export type SplitMode = 'auto' | 'antagonist' | 'ppl';

export interface Settings {
  splitMode: SplitMode;
  blockLengthWeeks: number;
  /** A deload lands on the last week of every Nth block. */
  deloadEveryNBlocks: number;
  equipment: string[];
  programStartDate: string;
  /** Show the generated illustrations on the workout screen. */
  showImages: boolean;
  /**
   * Pair accessory exercises so they run back-to-back. Cuts a good chunk off
   * session length at no real cost, as long as the paired movements don't
   * compete — turn it off if you'd rather take full rest on everything.
   */
  useSupersets: boolean;
  health: HealthSettings;
  /**
   * False until you've been through first-run setup. Your height and weight
   * feed the calorie targets directly, so the app asks rather than shipping a
   * guess — and it means no personal data is baked into the built bundle.
   */
  onboarded: boolean;
}

export interface HealthSettings {
  enabled: boolean;
  /** Blank means the same-origin /api/health endpoint. */
  endpoint: string;
  /** Must match HEALTH_TOKEN on the Cloudflare Pages project. */
  token: string;
  /** Turn imported treadmill/walk/run sessions into cardio entries. */
  autoImportWorkouts: boolean;
  lastSyncedAt?: string;
}

export interface ExercisePrefs {
  favorites: string[];
  excluded: string[];
}

export interface AppState {
  version: number;
  profile: Profile;
  settings: Settings;
  workouts: WorkoutSession[];
  cardio: CardioSession[];
  metrics: BodyMetric[];
  activeWorkout: WorkoutSession | null;
  exercisePrefs: ExercisePrefs;
  /** Daily totals pulled from Apple Health. */
  healthDays: HealthDay[];
  /** Health workout ids already turned into cardio entries, so we never double-log. */
  importedWorkoutIds: string[];
}

export const STATE_VERSION = 1;
